import { CompanyProduct } from 'src/app/models/company_product.model';
import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { UtilsService } from 'src/app/services/utils.service';
import dayjs from 'dayjs';
import { WorkingTimeHistoryDayDetailDialogComponent } from './working-time-history-day-detail-dialog/working-time-history-day-detail-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { User } from 'src/app/models/user.model';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete, MatOption } from '@angular/material/autocomplete';
import { AddNewWorkingTimeDialogComponent } from './add-new-working-time-dialog/add-new-working-time-dialog.component';
import { WorkingTime } from 'src/app/models/working_time.model';
import { PermissionsService } from 'src/app/services/permissions.service';
import { WorkingTimeSummary } from 'src/app/models/working-time-summary.model';
import { MatFormField, MatPrefix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { NgClass, DatePipe, AsyncPipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { SecondsToStringTimePipe } from '../../../../../pipes/seconds-to-string-time.pipe';
import { EmployeeService } from 'src/app/services';
import { map, Observable, startWith, filter, lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-working-times-history',
  templateUrl: './working-times-history.component.html',
  styleUrls: ['./working-times-history.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatPrefix,
    MatIcon,
    MatInput,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatIconButton,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    RouterLink,
    MatFooterCellDef,
    MatFooterCell,
    NgClass,
    MatTooltip,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    DatePipe,
    SecondsToStringTimePipe,
    AsyncPipe,
  ],
})
export class WorkingTimesHistoryComponent implements OnInit {
  private api = inject(ApiService);
  private user = inject(UserService);
  protected router = inject(Router);
  protected utils = inject(UtilsService);
  protected activatedRoute = inject(ActivatedRoute);
  protected fb = inject(UntypedFormBuilder);
  private dialog = inject(MatDialog);
  private permissions = inject(PermissionsService);
  private employee = inject(EmployeeService);

  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  filterForm: UntypedFormGroup;
  private formPersistence: any;

  userControl: UntypedFormControl = new UntypedFormControl();
  userAutocompleteControl: UntypedFormControl = new UntypedFormControl();
  usersFiltered: User[] = [];
  titleRowSpan: number = 1;
  private user_id: number = null as any;
  expectedHours: any = null;
  filteredOptions: Observable<User[]>;
  isAllowedToSeeOtherUsers: boolean = false;

  summary: WorkingTimeSummary | null = null;
  mainDisplayedColumnKeysDefault: string[] = ['category', 'sub_category'];
  mainDisplayedColumnKeys: string[] = this.mainDisplayedColumnKeysDefault.map(f => f);
  mainDataSource: MatTableDataSource<WorkingTimeCategoryLine> = new MatTableDataSource<WorkingTimeCategoryLine>();
  dayColumnHeaders: { key: string; header: string }[] = [];
  verticalExpectedSum: number = 0;
  horizontalExpectedSum: number = 0;

  actualUser: User;

  constructor() {
    this.filterForm = this.fb.group({
      user_id: [
        this.activatedRoute.snapshot.queryParamMap.get('user_id') != null
          ? +(this.activatedRoute.snapshot.queryParamMap.get('user_id') as any)
          : '',
        Validators.required,
      ],
      from: [
        this.activatedRoute.snapshot.queryParamMap.get('from') != null
          ? this.utils.stringToDate(this.activatedRoute.snapshot.queryParamMap.get('from') as string)
          : this.getInitOfWeekDate(),
        Validators.required,
      ],
      to: [
        this.activatedRoute.snapshot.queryParamMap.get('to') != null
          ? this.utils.stringToDate(this.activatedRoute.snapshot.queryParamMap.get('to') as string)
          : this.getEndOfWeekDate(),
        Validators.required,
      ],
    });
  }

  ngOnInit(): void {
    // Verificar permisos primero (síncrono)
    this.checkPermissions();

    this.listenUserService();
    this.initFilterFormListener();
    this.listenUserControl();
    this.listenQueryParameters();

    if (this.filterForm.value['user_id'] != '') this.fetchUser(this.filterForm.value['user_id']);
    else this.initCurrentUser(); // aixo es el que ho inicia tot
  }

  createColumnKeyByDate(d: Date | string) {
    const date = typeof d === 'string' ? new Date(d) : d;
    return dayjs(date).format('ddd-DD').toLowerCase();
  }

  getDayColumnHeader(d: Date | string) {
    const date = typeof d === 'string' ? new Date(d) : d;
    return dayjs(date).format('ddd DD');
  }

  openWorklogResume(worklogs: WorkingTime[]) {
    const dialogRef = this.dialog.open(WorkingTimeHistoryDayDetailDialogComponent, {
      width: '650px',
      data: {
        worklogs: worklogs,
      },
    });

    dialogRef.afterClosed().subscribe((do_fetch: boolean) => {
      if (do_fetch) {
        this.fetchWorkingTimeSummary();
      }
    });
  }

  changeWeek(num: number) {
    const old_from: Date = this.filterForm.value['from'];
    const new_from: Date = new Date(new Date(old_from).setDate(old_from.getDate() + num * 7));
    const new_to: Date = new Date(new Date(new_from).setDate(new_from.getDate() + 6));

    this.filterForm.patchValue({
      from: new_from,
      to: new_to,
    });
  }

  onUserAutocompleteSelected(event: MatAutocompleteSelectedEvent) {
    this.selectUser(event.option.value);
  }

  selectUser(user: User) {
    this.userControl.setValue(user);
  }

  displayFnUser(user: User): string {
    return user ? user.name + ' ' + user.surnames : '';
  }

  resetUserAutocompleteControl() {
    this.userAutocompleteControl.patchValue('');
    this.filterForm.patchValue({ user_id: null });
    // Limpiar el estado de la tabla
    this.summary = null;
    this.mainDataSource.data = [];
    this.dayColumnHeaders = [];
    this.verticalExpectedSum = 0;
    this.horizontalExpectedSum = 0;
  }

  private initFilterFormListener() {
    this.filterForm.valueChanges.subscribe(data => {
      if (this.formPersistence == null || JSON.stringify(this.formPersistence) != JSON.stringify(data)) {
        const params = this.utils.cloneObj(data);
        if (params.from != null && params.from != '') params.from = this.utils.dateToString(new Date(params.from));
        if (params.to != null && params.to != '') params.to = this.utils.dateToString(new Date(params.to));
        this.router.navigate(['/employees', 'activity', 'history'], { queryParams: params });
      }
    });
  }

  private listenQueryParameters() {
    // Suscribirse a cambios futuros
    this.activatedRoute.queryParams.subscribe(params => {
      if (JSON.stringify(params) !== JSON.stringify(this.filterForm.value)) {
        //si no ve de filtre s'ha de setejar el form
        const params_temp = this.utils.cloneObj(params); //params es read_only
        Object.keys(params_temp).forEach(param_key => {
          if (params_temp[param_key] != null && params_temp[param_key] != '' && !isNaN(+params_temp[param_key]))
            params_temp[param_key] = +params_temp[param_key]; // si es numero, el transformem
          else if (params_temp[param_key] != null && (params_temp[param_key] as string).indexOf('-') > -1)
            params_temp[param_key] = this.utils.stringToDate(params_temp[param_key]);
        });
        this.filterForm.patchValue(params_temp, { emitEvent: false });
      }
      this.formPersistence = params;
      // Pasar los params directamente a fetchWorkingTimeSummary para asegurar que use el user_id correcto
      this.fetchWorkingTimeSummary();
    });
  }

  private fetchWorkingTimeSummary() {
    // Denegar navegación por URL al usuario sin permisos
    const user_id: string = this.isAllowedToSeeOtherUsers ? this.filterForm.value['user_id'] : this.actualUser.id.toString();

    // Limpiar el estado anterior antes de cargar nuevos datos
    this.summary = null;
    this.mainDataSource.data = [];
    this.dayColumnHeaders = [];
    this.verticalExpectedSum = 0;
    this.horizontalExpectedSum = 0;

    if (user_id != null && user_id != '') {
      this.api.getWorkingTimeSummary(user_id, this.filterForm.value['from'], this.filterForm.value['to']).subscribe({
        next: data => {
          if (data != undefined) {
            this.summary = data;
            this.initTable(data);
            this.initColumns(data);
          }
        },
        error: error => {
          console.error('Error fetching working time summary:', error);
        },
      });
    }
  }

  private initCurrentUser() {
    this.user.currentUser.subscribe(data => {
      if (this.filterForm.value['user_id'] == null || this.filterForm.value['user_id'] == '') {
        this.selectUser(data);
        this.usersFiltered = [data];
        this.userAutocompleteControl.setValue(data);
      }
    });
  }

  private getInitOfWeekDate(now?: Date) {
    return dayjs(now).startOf('week').toDate();
  }

  private getEndOfWeekDate(now?: Date) {
    return dayjs(now).endOf('week').toDate();
  }

  private listenUserControl() {
    this.userControl.valueChanges.subscribe(user => {
      if (this.user_id == null || this.user_id != user.id) {
        this.user_id = user.id;
        this.userAutocompleteControl.setValue(user);
        this.filterForm.patchValue({ user_id: user.id });

        if (!this.isAllowedToSeeOtherUsers) {
          this.userAutocompleteControl.disable();
          this.userControl.disable();
        }
      }
    });
  }

  private listenUserAutocompleteControl() {
    if (this.isAllowedToSeeOtherUsers) {
      this.filteredOptions = this.userAutocompleteControl.valueChanges.pipe(
        startWith(''),
        map(value => this._filter(value || ''))
      );
    }
  }

  private _filter(value: any): User[] {
    let userName = value;
    if (typeof value == 'object') {
      userName = `${value.name} ${value.surnames}`;
    }
    const filterValue = userName.toLocaleLowerCase();

    return this.usersFiltered.filter(user => (user.name + ' ' + user.surnames).toLowerCase().includes(filterValue));
  }

  private fetchUser(id: string) {
    this.api
      .getUser(id)
      .toPromise()
      .then(data => {
        if (data != undefined) {
          this.selectUser(data);
          this.userAutocompleteControl.setValue(data);
        }
      });
  }

  private listenUserService() {
    this.actualUser = this.user.getCurrentUser();

    // Verificar permisos inmediatamente
    this.checkPermissions();

    // También verificar permisos después de un delay para capturar cambios asíncronos
    setTimeout(() => {
      this.checkPermissions();
    }, 1000);
  }

  private checkPermissions() {
    if (
      this.permissions.validateDepartment(['direction', 'rrhh']) ||
      this.permissions.isTechLead() ||
      this.permissions.isProductManager()
    ) {
      this.isAllowedToSeeOtherUsers = true;
      // Inicializar la lista de usuarios cuando los permisos estén listos
      this.initUserList();
    }
  }

  private initTable(data: WorkingTimeSummary) {
    const res: WorkingTimeCategoryLine[] = [];

    // Process the new format with children
    this.processNewFormat(data, res);

    // Precalculate all data for each line
    res.forEach(line => {
      this.precalculateLineData(line, res);
    });

    // Precalculate summary data
    this.precalculateSummaryData(res, data);

    this.mainDataSource.data = res;
  }

  private precalculateLineData(line: WorkingTimeCategoryLine, allLines: WorkingTimeCategoryLine[]) {
    // Precalculate spaces array
    line.spacesArray = Array.from({ length: line.level || 0 }, (_, i) => i);

    // Precalculate hasOwnExpected
    line.hasOwnExpected = this.calculateHasOwnExpected(line, allLines);

    // Precalculate background colors
    line.backgroundColor = line.type === 'company_product' || line.type === 'category' ? '#f8f9fa' : 'white';
    line.rowBackgroundColor = line.type === 'company_product' || line.type === 'category' ? '#f8f9fa' : 'white';

    // Precalculate day data
    if (line.days) {
      line.days = line.days.map(day => ({
        ...day,
        isClickable: (day.worklogs?.length || 0) > 0,
        tooltipText: (day.worklogs?.length || 0) > 0 ? 'Ver detalle de worklogs' : '',
      }));
    }
  }

  private calculateHasOwnExpected(line: WorkingTimeCategoryLine, allLines: WorkingTimeCategoryLine[]): boolean {
    if ((line.level || 0) === 0) {
      return true; // Primer nivel siempre tiene expected propio
    }

    const childrenSum = this.getChildrenExpectedSum(line, allLines);
    const lineExpected = line.total_seconds_subcat_expected;

    return lineExpected !== childrenSum;
  }

  private getChildrenExpectedSum(line: WorkingTimeCategoryLine, allLines: WorkingTimeCategoryLine[]): number {
    const lineIndex = allLines.indexOf(line);
    const lineLevel = line.level || 0;

    // Buscar todas las líneas que son hijas directas de esta línea
    const childrenLines = allLines.filter((childLine, childIndex) => {
      const childLevel = childLine.level || 0;

      // Es hija directa si:
      // 1. Tiene un nivel mayor (level + 1)
      // 2. Está después de la línea padre
      // 3. No hay otra línea del mismo nivel entre el padre y el hijo
      const isDirectChild = childLevel === lineLevel + 1;
      const isAfterParent = childIndex > lineIndex;

      // Verificar que no hay otra línea del mismo nivel entre el padre y el hijo
      let hasIntermediateSibling = false;
      if (isAfterParent) {
        for (let i = lineIndex + 1; i < childIndex; i++) {
          const intermediateLine = allLines[i];
          if ((intermediateLine.level || 0) <= lineLevel) {
            hasIntermediateSibling = true;
            break;
          }
        }
      }

      return isDirectChild && isAfterParent && !hasIntermediateSibling;
    });

    const sum = childrenLines.reduce((s, childLine) => s + childLine.total_seconds_subcat_expected, 0);
    return sum;
  }

  private precalculateSummaryData(allLines: WorkingTimeCategoryLine[], data: WorkingTimeSummary) {
    // Precalculate vertical expected sum
    this.verticalExpectedSum = allLines.filter(line => line.level === 0).reduce((sum, line) => sum + line.total_seconds_expected, 0);

    // Precalculate horizontal expected sum
    this.horizontalExpectedSum = (data.days || []).reduce((sum: number, day: any) => {
      return sum + (day.total_hours || 0) * 3600;
    }, 0);

    // Precalculate day column headers
    const daysArray = data.period || [];
    this.dayColumnHeaders = daysArray.map((day: any) => {
      const dayDate = typeof day === 'string' ? new Date(day) : day;
      return {
        key: this.createColumnKeyByDate(dayDate),
        header: this.getDayColumnHeader(dayDate),
      };
    });
  }

  private processNewFormat(data: any, res: WorkingTimeCategoryLine[]) {
    // Process the new format with children
    const children = data.children || {};

    // Process each top-level item (categories and company_products)
    Object.keys(children).forEach(key => {
      const child = children[key];

      if (key.includes('WorkingTimeCategory_')) {
        // This is a top-level category
        res.push({
          flag: 'category',
          type: 'category',
          primary_entity_id: child.working_time_category_id,
          primary_entity_title: child.working_time_category_name,
          secondary_entity_id: null,
          secondary_entity_title: null,
          days: this.createDaysFromCategoryData(data, child),
          total_seconds_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_expected: this.hoursToSeconds(child.expected_hours || 0),
          is_first_subcategory: true,
          secondary_items: 0,
          level: 0,
          total_seconds_subcat_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_subcat_expected: this.hoursToSeconds(child.expected_hours || 0),
          deep: null,
          parent_deep: null,
          categories: null,
        });
      } else if (key.includes('CompanyProduct_')) {
        // This is a company_product - add it first
        res.push({
          flag: 'company_product',
          type: 'company_product',
          primary_entity_id: child.company_product_id,
          primary_entity_title: child.company_product_title,
          secondary_entity_id: null,
          secondary_entity_title: null,
          days: this.createDaysFromCompanyProductData(data, child),
          total_seconds_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_expected: this.hoursToSeconds(child.expected_hours || 0),
          is_first_subcategory: true,
          secondary_items: child.children ? Object.keys(child.children).length : 0,
          level: 0,
          total_seconds_subcat_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_subcat_expected: this.hoursToSeconds(child.expected_hours || 0),
          deep: null,
          parent_deep: child.children ? Object.keys(child.children).length : null,
          categories: null,
        });

        // Then add its subcategories recursively
        if (child.children) {
          this.processChildrenRecursively(child.children, res, data, 1, child.company_product_id?.toString());
        }
      }
    });
  }

  private processChildrenRecursively(
    children: any,
    res: WorkingTimeCategoryLine[],
    data: any,
    level: number,
    companyProductId?: string,
    parentCategoryId?: string,
    parentPhaseId?: string
  ) {
    Object.keys(children).forEach((childKey, index) => {
      const child = children[childKey];

      if (childKey.includes('WorkingTimeCategory_')) {
        // This is a subcategory
        const categoryId = child.working_time_category_id?.toString() || '0';
        res.push({
          flag: 'subcategory',
          type: 'subcategory',
          primary_entity_id: child.working_time_category_id,
          primary_entity_title: child.working_time_category_name,
          secondary_entity_id: null,
          secondary_entity_title: child.working_time_category_name,
          days: this.createDaysFromData(data, categoryId, 'subcategory', companyProductId, parentCategoryId, parentPhaseId),
          total_seconds_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_expected: this.hoursToSeconds(child.expected_hours || 0),
          is_first_subcategory: index === 0,
          secondary_items: child.children ? Object.keys(child.children).length : 1,
          level: level,
          total_seconds_subcat_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_subcat_expected: this.hoursToSeconds(child.expected_hours || 0),
          deep: null,
          parent_deep: child.children ? Object.keys(child.children).length : null,
          categories: null,
        });

        // Process sub-subcategories recursively - pasar el categoryId como parentCategoryId
        // Si estamos dentro de una fase, mantener el parentPhaseId para que los hijos busquen dentro de la fase
        if (child.children) {
          this.processChildrenRecursively(child.children, res, data, level + 1, companyProductId, categoryId, parentPhaseId);
        }
      } else if (childKey.includes('CompanyProductPhase_')) {
        // This is a company_product phase
        // Para las fases, necesitamos pasar tanto el phaseId como el categoryId del padre
        // El parentCategoryId debe ser el ID de la categoría que contiene la fase
        // SIEMPRE extraer el phaseId del childKey para asegurar que cada fase use su ID único
        // El childKey siempre será único: "App\Models\CompanyProductPhase_36" vs "App\Models\CompanyProductPhase_71"
        const match = childKey.match(/CompanyProductPhase_(\d+)/);
        const phaseId = match ? match[1] : child.company_product_phase_id?.toString() || '0';

        // Debug: verificar que cada fase usa su ID único
        // console.log('Processing phase:', {
        //   childKey,
        //   phaseId,
        //   company_product_phase_id: child.company_product_phase_id,
        //   company_product_phase_title: child.company_product_phase_title,
        //   companyProductId,
        //   parentCategoryId,
        //   done_hours: child.done_hours
        // });

        res.push({
          flag: 'phase',
          type: 'phase',
          primary_entity_id: child.company_product_phase_id || parseInt(phaseId),
          primary_entity_title: child.company_product_phase_title,
          secondary_entity_id: null,
          secondary_entity_title: child.company_product_phase_title,
          days: this.createDaysFromData(data, phaseId, 'phase', companyProductId, parentCategoryId),
          total_seconds_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_expected: this.hoursToSeconds(child.expected_hours || 0),
          is_first_subcategory: index === 0,
          secondary_items: child.children ? Object.keys(child.children).length : 1,
          level: level,
          total_seconds_subcat_done: this.hoursToSeconds(child.done_hours || 0),
          total_seconds_subcat_expected: this.hoursToSeconds(child.expected_hours || 0),
          deep: null,
          parent_deep: child.children ? Object.keys(child.children).length : null,
          categories: null,
        });

        // Process phase subcategories recursively
        // IMPORTANTE: Cuando una fase tiene hijos (subcategorías), debemos pasar el phaseId como parentPhaseId
        // para que esas subcategorías y sus hijos busquen sus datos dentro de la fase
        if (child.children) {
          // Pasar el phaseId como parentPhaseId para que las subcategorías dentro de la fase
          // y sus hijos busquen los datos dentro de la estructura de la fase
          this.processChildrenRecursively(child.children, res, data, level + 1, companyProductId, parentCategoryId, phaseId);
        }
      }
    });
  }

  private initColumns(data: WorkingTimeSummary) {
    this.mainDisplayedColumnKeys = this.mainDisplayedColumnKeysDefault.map(f => f);

    // Use precalculated day column headers
    this.dayColumnHeaders.forEach(dayCol => {
      this.mainDisplayedColumnKeys.push(dayCol.key);
    });

    this.mainDisplayedColumnKeys.push('total_company_product');
  }

  private createEmptyDays(daysCount: number): any[] {
    return Array(daysCount)
      .fill(null)
      .map(() => ({
        total_seconds_done: 0,
        total_seconds_expected: 0,
        worklogs: [],
        flag: null,
      }));
  }

  private createDaysFromCategoryData(data: any, category: any): any[] {
    if (!data.days || !Array.isArray(data.days)) {
      return this.createEmptyDays(data.period?.length || 7);
    }

    return data.days.map((dayData: any) => {
      // Buscar la categoría específica en este día
      const categoryKey = `App\\Models\\WorkingTimeCategory_${category.working_time_category_id}`;
      const categoryDayData = dayData.children?.[categoryKey];

      if (categoryDayData) {
        return {
          total_seconds_done: this.hoursToSeconds(categoryDayData.done_hours || 0),
          total_seconds_expected: this.hoursToSeconds(categoryDayData.expected_hours || categoryDayData.total_hours || 0),
          worklogs: categoryDayData.worklogs || categoryDayData.working_times || [],
          flag: categoryDayData.flag || null,
        };
      }

      return {
        total_seconds_done: 0,
        total_seconds_expected: 0,
        worklogs: [],
        flag: null,
      };
    });
  }

  private createDaysFromCompanyProductData(data: any, company_product: any): any[] {
    if (!data.days || !Array.isArray(data.days)) {
      return this.createEmptyDays(data.period?.length || 7);
    }

    return data.days.map((dayData: any) => {
      // Buscar el producto específico en este día
      const companyProductKey = `App\\Models\\CompanyProduct_${company_product.company_product_id}`;
      const companyProductDayData = dayData.children?.[companyProductKey];

      if (companyProductDayData) {
        return {
          total_seconds_done: this.hoursToSeconds(companyProductDayData.done_hours || 0),
          total_seconds_expected: this.hoursToSeconds(companyProductDayData.expected_hours || companyProductDayData.total_hours || 0),
          worklogs: companyProductDayData.worklogs || companyProductDayData.working_times || [],
          flag: companyProductDayData.flag || null,
        };
      }

      return {
        total_seconds_done: 0,
        total_seconds_expected: 0,
        worklogs: [],
        flag: null,
      };
    });
  }

  private findEntityInChildren(children: any, searchKey: string): any {
    // Buscar directamente en este nivel
    if (children[searchKey]) {
      return children[searchKey];
    }

    // Buscar recursivamente en todos los children
    for (const key in children) {
      const child = children[key];
      if (child && child.children) {
        const found = this.findEntityInChildren(child.children, searchKey);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  private createDaysFromData(
    data: any,
    entityId: string,
    entityType: 'category' | 'company_product' | 'subcategory' | 'phase',
    companyProductId?: string,
    parentCategoryId?: string,
    parentPhaseId?: string
  ): any[] {
    if (!data.days || !Array.isArray(data.days)) {
      return this.createEmptyDays(data.period?.length || 7);
    }

    return data.days.map((dayData: any) => {
      // Buscar los datos específicos de esta entidad en el día
      let entityDayData = null;

      if (dayData.children) {
        // Construir la clave esperada según el tipo de entidad
        let searchKey = '';
        if (entityType === 'category') {
          searchKey = `App\\Models\\WorkingTimeCategory_${entityId}`;
          entityDayData = dayData.children[searchKey];
        } else if (entityType === 'company_product') {
          searchKey = `App\\Models\\CompanyProduct_${entityId}`;
          entityDayData = dayData.children[searchKey];
        } else if (entityType === 'subcategory') {
          // Para subcategorías, buscar recursivamente en toda la estructura del producto
          // Si hay un parentPhaseId, buscar dentro de la fase: company_product.children[categoryKey].children[phaseKey].children[subcategoryKey]
          if (companyProductId) {
            const companyProductKey = `App\\Models\\CompanyProduct_${companyProductId}`;
            const company_product = dayData.children[companyProductKey];
            if (company_product && company_product.children) {
              if (parentPhaseId && parentCategoryId) {
                // Buscar dentro de la fase
                const categoryKey = `App\\Models\\WorkingTimeCategory_${parentCategoryId}`;
                const category = company_product.children[categoryKey];
                if (category && category.children) {
                  const phaseKey = `App\\Models\\CompanyProductPhase_${parentPhaseId}`;
                  const phase = category.children[phaseKey];
                  if (phase && phase.children) {
                    const subcategoryKey = `App\\Models\\WorkingTimeCategory_${entityId}`;
                    entityDayData = phase.children[subcategoryKey];
                  }
                }
              } else {
                // Buscar recursivamente en toda la estructura del producto (caso normal)
                entityDayData = this.findEntityInChildren(company_product.children, `App\\Models\\WorkingTimeCategory_${entityId}`);
              }
            }
          }
        } else if (entityType === 'phase') {
          // Para fases, buscar dentro de la categoría padre dentro del producto
          // Estructura: days[0]['children']['App\Models\CompanyProduct_X']['children']['App\Models\WorkingTimeCategory_Y']['children']['App\Models\CompanyProductPhase_Z']
          if (companyProductId && parentCategoryId) {
            const companyProductKey = `App\\Models\\CompanyProduct_${companyProductId}`;
            const company_product = dayData.children[companyProductKey];
            if (company_product && company_product.children) {
              const categoryKey = `App\\Models\\WorkingTimeCategory_${parentCategoryId}`;
              const category = company_product.children[categoryKey];
              if (category && category.children) {
                const phaseKey = `App\\Models\\CompanyProductPhase_${entityId}`;
                entityDayData = category.children[phaseKey];

                // Debug: verificar qué fase se está buscando y si se encontró
                // console.log('Searching for phase:', {
                //   companyProductId,
                //   parentCategoryId,
                //   entityId,
                //   phaseKey,
                //   found: !!entityDayData,
                //   done_hours: entityDayData ? (entityDayData as any).done_hours : null
                // });
              }
            }
          }
        }
      }

      // Mapear correctamente los datos encontrados
      if (entityDayData) {
        let doneHours = 0;
        let expectedHours = 0;
        let worklogs = [];
        let flag = null;

        // Los datos pueden estar directamente en entityDayData o dentro de children
        // Primero verificar si los datos están directamente en entityDayData
        // Nota: done_hours puede ser 0, así que verificamos !== undefined en lugar de truthy
        const hasDirectData =
          (entityDayData as any).done_hours !== undefined ||
          (entityDayData as any).working_times !== undefined ||
          (entityDayData as any).worklogs !== undefined;

        if (hasDirectData) {
          // Datos directamente en el objeto encontrado
          doneHours = (entityDayData as any).done_hours || 0;
          expectedHours = (entityDayData as any).expected_hours || (entityDayData as any).total_hours || 0;
          // Priorizar working_times sobre worklogs ya que es el nombre usado en el JSON
          worklogs = (entityDayData as any).working_times || (entityDayData as any).worklogs || [];
          flag = (entityDayData as any).flag || null;
        } else if ((entityDayData as any).children) {
          // Si no hay datos directos, buscar en children
          const childKey = `App\\Models\\WorkingTimeCategory_${entityId}`;
          const childData = (entityDayData as any).children[childKey];
          if (childData) {
            doneHours = childData.done_hours || 0;
            expectedHours = childData.expected_hours || childData.total_hours || 0;
            worklogs = childData.working_times || childData.worklogs || [];
            flag = childData.flag || null;
          }
        }

        return {
          total_seconds_done: doneHours * 3600,
          total_seconds_expected: expectedHours * 3600,
          worklogs: worklogs,
          flag: flag,
        };
      } else {
        return {
          total_seconds_done: 0,
          total_seconds_expected: 0,
          worklogs: [],
          flag: null,
        };
      }
    });
  }

  // These methods are kept for backward compatibility but data should be precalculated
  getSpacesArray(level: number): number[] {
    return Array.from({ length: level || 0 }, (_, i) => i);
  }

  hasOwnExpected(line: WorkingTimeCategoryLine): boolean {
    return line.hasOwnExpected ?? false;
  }

  private hoursToSeconds(hours: number): number {
    return Math.round(hours * 3600);
  }

  // These methods are kept for backward compatibility but data should be precalculated
  getVerticalExpectedSum(): number {
    return this.verticalExpectedSum;
  }

  getHorizontalExpectedSum(): number {
    return this.horizontalExpectedSum;
  }

  private async initUserList() {
    if (this.permissions.validateDepartment(['rrhh', 'direction'])) {
      const params: any = {
        from: dayjs(this.filterForm.value['from']).format('YYYY-MM-DD'),
        to: dayjs(this.filterForm.value['to']).format('YYYY-MM-DD'),
      };
      await lastValueFrom(this.api.getActiveEmployees(params)).then(users => {
        if (users != undefined) {
          this.usersFiltered = users;
          this.listenUserAutocompleteControl();
        }
      });
    } else if (this.permissions.isTechLead() || this.permissions.isProductManager()) {
      const params = {
        user_id: this.actualUser.id,
      };
      this.api.getCompanyProducts(params).subscribe(company_products => {
        for (const company_product of company_products.data) {
          // Solo tendremos acceso a usuarios de nuestros productos
          this.api.getActiveCompanyProductMembers(company_product.id.toString(), { role: 'worker' }).subscribe(res => {
            const users = res.map(r => r.user);
            this.usersFiltered = [
              ...this.usersFiltered,
              ...users.filter(newUser => !this.usersFiltered.some(oldUser => oldUser.id === newUser.id)),
            ];
            this.listenUserAutocompleteControl();
          });
        }
      });
    }
  }
}

interface WorkingTimeCategoryLine {
  type: string;
  primary_entity_id: number;
  primary_entity_title: string;
  secondary_entity_id: number | null;
  secondary_entity_title: string | null;
  days:
    | undefined
    | {
        total_seconds_done: number;
        total_seconds_expected: number;
        worklogs: WorkingTime[];
        flag: string | null;
        isClickable?: boolean;
        tooltipText?: string;
      }[];
  total_seconds_done: number;
  total_seconds_expected: number;
  total_seconds_subcat_done: number;
  total_seconds_subcat_expected: number;
  is_first_subcategory: boolean;
  secondary_items: number;
  categories: number | null;
  deep: number | null;
  level: number | null;
  parent_deep: number | null;
  flag: string;
  spacesArray?: number[];
  hasOwnExpected?: boolean;
  backgroundColor?: string;
  rowBackgroundColor?: string;
}
